import { logger } from "../utils/logger";

/**
 * Field Mapping Configuration
 * Maps frontend form fields to their corresponding database tables and columns
 * This ensures we send updates to the correct normalized tables
 */

export interface FieldMapping {
  table: string;
  column: string;
  transform?: (value: any) => any;
}

/**
 * Complete field-to-table mapping based on normalized database schema
 * Table names match exact database table names
 */
export const PATIENT_FIELD_MAPPING: Record<string, FieldMapping> = {
  // patients table
  title: { table: 'patients', column: 'title' },
  first_name: { table: 'patients', column: 'first_name' },
  last_name: { table: 'patients', column: 'last_name' },
  dateOfBirth: { table: 'patients', column: 'date_of_birth' },
  gender: { table: 'patients', column: 'gender' },
  bloodGroup: { table: 'patients', column: 'blood_group' },
  maritalStatus: { table: 'patients', column: 'marital_status' },
  occupation: { table: 'patients', column: 'occupation' },
  company: { table: 'patients', column: 'company' },
  
  // patient_contacts table
  mobileNumber: { 
    table: 'patient_contacts', 
    column: 'contact_number',
    transform: (value: string) => value ? `+91${value.replace(/^\+91/, '')}` : ''
  },
  email: { table: 'patient_contacts', column: 'email' },
  alternateNumber: { 
    table: 'patient_contacts', 
    column: 'alternate_contact_number',
    transform: (value: string) => value ? `+91${value.replace(/^\+91/, '')}` : ''
  },
  aadhar_number: { table: 'patient_contacts', column: 'aadhar_number' },
  abhaAddress: { table: 'patient_contacts', column: 'abha_id' },
  
  // patient_addresses table
  address: { table: 'patient_addresses', column: 'address' },
  city: { table: 'patient_addresses', column: 'city' },
  state: { table: 'patient_addresses', column: 'state' },
  pincode: { table: 'patient_addresses', column: 'pincode' },
  
  // insurance table
  insuranceProvider: { table: 'insurance', column: 'provider' },
  insurancePolicyNumber: { table: 'insurance', column: 'policy_number' },
  insurancePolicyHolderName: { table: 'insurance', column: 'policy_holder_name' },
  insurancePolicyExpiryDate: { table: 'insurance', column: 'policy_expiry_date' },
  insuranceCoverageAmount: { table: 'insurance', column: 'coverage_amount' },
  
  // medical_profiles table
  currentMedications: { table: 'medical_profiles', column: 'current_medications' },
  previousSurgeries: { table: 'medical_profiles', column: 'previous_surgeries' },
  medicalConditions: { 
    table: 'medical_profiles', 
    column: 'medical_conditions',
    transform: (value: any) => typeof value === 'string' ? value : JSON.stringify(value)
  },
  lifestyleOptions: { 
    table: 'medical_profiles', 
    column: 'lifestyle_options',
    transform: (value: any) => typeof value === 'string' ? value : JSON.stringify(value)
  },
  medicalHistoryNotes: { table: 'medical_profiles', column: 'medical_history_notes' },
  
  // patient_preferences table
  appointmentConfirmations: { table: 'patient_preferences', column: 'appointment_confirmations' },
  appointmentReminders: { table: 'patient_preferences', column: 'appointment_reminders' },
  prescriptionReady: { table: 'patient_preferences', column: 'prescription_ready' },
  paymentReceived: { table: 'patient_preferences', column: 'payment_received' },
  paymentReminders: { table: 'patient_preferences', column: 'payment_reminders' },
  systemUpdates: { table: 'patient_preferences', column: 'system_updates' },
  emailFrequency: { table: 'patient_preferences', column: 'email_frequency' },
  quietHours: { table: 'patient_preferences', column: 'quiet_hours' },
  
  // emergency_contacts table (array)
  emergencyContacts: { table: 'emergency_contacts', column: 'emergency_contacts' },
};

/**
 * Get the table name for a given form field
 */
export const getTableForField = (fieldName: string): string | null => {
  const mapping = PATIENT_FIELD_MAPPING[fieldName];
  return mapping ? mapping.table : null;
};

/**
 * Get the database column name for a given form field
 */
export const getColumnForField = (fieldName: string): string | null => {
  const mapping = PATIENT_FIELD_MAPPING[fieldName];
  return mapping ? mapping.column : null;
};

/**
 * Transform a form field value to its database format
 */
export const transformFieldValue = (fieldName: string, value: any): any => {
  const mapping = PATIENT_FIELD_MAPPING[fieldName];
  if (mapping && mapping.transform) {
    return mapping.transform(value);
  }
  return value;
};

/**
 * Build a partial update payload from changed fields
 * Groups fields by their database table
 */
export const buildPartialUpdatePayload = (changes: Record<string, any>): Record<string, any> => {
  const payload: Record<string, any> = {};
  
  Object.entries(changes).forEach(([fieldName, value]) => {
    const mapping = PATIENT_FIELD_MAPPING[fieldName];
    
    if (!mapping) {
      logger.warn(`No mapping found for field: ${fieldName}`);
      return;
    }
    
    const { table, column, transform } = mapping;
    
    // Initialize table object if it doesn't exist
    if (!payload[table]) {
      payload[table] = {};
    }
    
    // Transform value if transformer exists
    const transformedValue = transform ? transform(value) : value;
    
    // Add to payload
    payload[table][column] = transformedValue;
  });
  
  return payload;
};

/**
 * Compare two values and determine if they've changed
 * Handles null, undefined, empty strings, and complex objects
 */
export const hasFieldChanged = (original: any, current: any): boolean => {
  // Normalize empty values
  const normalizeEmpty = (val: any) => {
    if (val === null || val === undefined || val === '') return null;
    return val;
  };
  
  const origNormalized = normalizeEmpty(original);
  const currNormalized = normalizeEmpty(current);
  
  // Both are empty, no change
  if (origNormalized === null && currNormalized === null) return false;
  
  // One is empty, one is not
  if (origNormalized === null || currNormalized === null) return true;
  
  // For objects/arrays, compare as JSON strings
  if (typeof origNormalized === 'object' && typeof currNormalized === 'object') {
    return JSON.stringify(origNormalized) !== JSON.stringify(currNormalized);
  }
  
  // For booleans, compare directly
  if (typeof origNormalized === 'boolean' && typeof currNormalized === 'boolean') {
    return origNormalized !== currNormalized;
  }
  
  // For primitives, compare as strings
  return String(origNormalized) !== String(currNormalized);
};
